import React from 'react';
import { Card, CardContent, CardHeader } from '@/designSystem/Card';
import { PropertyImage } from '@/features/property/components/PropertyImage';
import { PropertyCardContent } from '@/features/map/components/PropertyCardContent';
import { SectionDivider } from '@/designSystem/SectionDivider';
import { Button } from '@/designSystem/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/designSystem/Dialog";
import { BaseProperty, InternalProperty } from '@/features/property/types';
import { Competitor } from '@/features/compset/types';
import { getCurrencySymbolFromCountryCode } from '@/utils';
import MatchingTable from '@/features/compset/components/MatchingTable';
import { RealtimeProperty } from '@/features/realtimeProperty/types';
import { ChartProperty } from '@/features/charts/types';

interface PropertyMapListProps {
  ownProperty: RealtimeProperty | ChartProperty;
  competitors: Competitor[];
  marketProperties: BaseProperty[];
  onPropertyClick: (property: BaseProperty) => void;
  selectedPropertyId: number | undefined;
}

const getSortedCompetitors = (competitors: Competitor[]): Competitor[] => {
  return [...competitors].sort((a, b) => {
    return b.competitor_score - a.competitor_score;
  });
};

// Component for displaying property metrics
const PropertyMetrics: React.FC<{
  property: RealtimeProperty | ChartProperty | Competitor;
  propertyType: 'ownProperty' | 'competitor' | 'market';
}> = ({ property, propertyType }) => {
  const currencySymbol = getCurrencySymbolFromCountryCode(property.country_code);
  const isCompetitor = propertyType === 'competitor';
  const isOwn = propertyType === 'ownProperty';

  // Get metrics based on property type
  let metricsData: any = null;

  if (isOwn) {
    const ownProperty = property as RealtimeProperty | InternalProperty;

    metricsData = {
      bedrooms: ownProperty.metrics.nb_bedrooms || '-',
      adr: ownProperty.metrics.adr,
      review_score: ownProperty.metrics.review_score,
      availability: ownProperty.metrics.availability,
      rank: ownProperty.metrics.rank,
      review_count: ownProperty.metrics.review_count || '-',
    };
  } else if (isCompetitor) {
    const comp = property as Competitor;
    const matchingDetails = comp.matching_details;

    metricsData = {
      bedrooms: matchingDetails?.values?.nb_bedrooms || '-',
      adr: matchingDetails?.values?.adr,
      review_score: matchingDetails?.values?.review_score,
      availability: matchingDetails?.values?.matching_days,
      rank: matchingDetails?.values?.rank,
      distance: comp.distance_km ? `${comp.distance_km.toFixed(1)} km` : '-',
      review_count: matchingDetails?.values?.review_count || '-',
    };
  }

  if (!metricsData) return null;

  return (
    <div className="bg-gray-50 rounded-md p-3 mx-2 my-2">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-600">Bedrooms:</span>
          <span className="font-medium">{metricsData.bedrooms}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Review Count:</span>
          <span className="font-medium">{metricsData.review_count}</span>
        </div>

        {!isOwn && (
          <div className="flex justify-between">
            <span className="text-gray-600">Distance:</span>
            <span className="font-medium">{metricsData.distance}</span>
          </div>
        )}

        <div className="flex justify-between">
          <span className="text-gray-600">Price (ADR):</span>
          <span className="font-medium">
            {metricsData.adr ? `${currencySymbol}${Math.round(metricsData.adr)}` : '-'}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-600">Review Score:</span>
          <span className="font-medium">
            {metricsData.review_score ? metricsData.review_score.toFixed(1) : '-'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Ranking Position:</span>
          <span className="font-medium">{metricsData.rank ? `#${metricsData.rank}` : '-'}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-600">Matching Available Days:</span>
          <span className="font-medium">{metricsData.availability || '-'}</span>
        </div>
      </div>
    </div>
  );
};

const PropertyMapList: React.FC<PropertyMapListProps> = ({
  ownProperty,
  competitors,
  marketProperties,
  onPropertyClick,
  selectedPropertyId,
}) => {
  const sortedCompetitors = getSortedCompetitors(competitors);

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="border-b py-3 px-4">
        <h3 className="text-lg font-semibold">Properties</h3>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto p-3">
        {ownProperty && (
          <div
            className={`
              mb-4 rounded-md shadow-sm border-2 border-green-500 overflow-hidden 
              hover:shadow-md transition-shadow cursor-pointer relative
              ${selectedPropertyId === ownProperty.property_id ? 'bg-green-50 ring-2 ring-green-300' : ''}
            `}
            onClick={() => onPropertyClick(ownProperty)}
          >
            <div className='flex'>
              <div className='w-36 h-36 flex-shrink-0'>
                <PropertyImage photoPath={ownProperty.photo_path} />
              </div>

              <PropertyCardContent
                property={ownProperty}
                propertyType="ownProperty"
              />
            </div>

            {/* Metrics for own property */}
            <PropertyMetrics property={ownProperty} propertyType="ownProperty" />
          </div>
        )}

        {sortedCompetitors.length > 0 && (
          <SectionDivider label="Competitors" />
        )}

        {sortedCompetitors.map((property) => {
          const isSelected = selectedPropertyId === property.property_id;

          return (
            <div
              key={property.property_id}
              className={`
                mb-3 rounded-md transition-all border overflow-hidden
                ${isSelected ? 'ring-2 ring-blue-200 bg-blue-50/30 border-blue-300' : 'ring-1 ring-gray-200 hover:ring-blue-200 border-gray-200'}
              `}
            >
              {/* Main property card with image and basic details */}
              <div
                className='flex cursor-pointer'
                onClick={() => onPropertyClick(property)}
              >
                <div className='w-36 h-36 flex-shrink-0'>
                  <PropertyImage photoPath={property.photo_path} />
                </div>

                <PropertyCardContent
                  property={property}
                  propertyType="competitor"
                />
              </div>

              {/* Metrics for competitor */}
              <PropertyMetrics property={property} propertyType="competitor" />

              {/* Modal trigger for detailed comparison */}
              {property.matching_details && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full flex items-center justify-center py-1.5 text-xs rounded-b-md rounded-t-none border-t"
                    >
                      View Detailed Comparison
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto z-[10000]">
                    <DialogHeader>
                      <DialogTitle>Competitor Similarity Analysis: {property.name}</DialogTitle>
                    </DialogHeader>
                    <MatchingTable
                      matchingDetails={property.matching_details}
                      currencySymbol={getCurrencySymbolFromCountryCode(property.country_code)}
                      distanceKM={property.distance_km}
                    />
                  </DialogContent>
                </Dialog>
              )}
            </div>
          );
        })}

        {marketProperties.length > 0 && (
          <SectionDivider label="Market Properties in Area" />
        )}

        {marketProperties.map((property) => {
          const isSelected = selectedPropertyId === property.property_id;

          return (
            <div
              key={property.property_id}
              className={`
                flex gap-1 mb-3 rounded-md transition-all overflow-hidden border
                ${isSelected ? 'ring-2 ring-blue-200 bg-blue-50/30 border-blue-300' : 'ring-1 ring-gray-200 hover:ring-blue-200 border-gray-200'}
              `}
              onClick={() => onPropertyClick(property)}
            >
              <div className='w-36 h-36 flex-shrink-0'>
                <PropertyImage photoPath={property.photo_path} />
              </div>
              <PropertyCardContent
                property={property}
                propertyType="market"
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default PropertyMapList;